import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Users, Plus, Trash2, Edit, Upload, Download, Phone, FileSpreadsheet, Receipt, MessageSquare, Search, Clock, FileText, Key, CheckCircle } from "lucide-react";
import { formatSloDate } from "../lib/dateUtils";

const MembersPage = () => {
  const { user, token, hasRole } = useAuth();
  const [members, setMembers] = useState([]);
  const [debtsSummary, setDebtsSummary] = useState([]);
  const [ldGroups, setLdGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [debtDialogOpen, setDebtDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedMemberForDebt, setSelectedMemberForDebt] = useState(null);
  const [selectedMemberForHours, setSelectedMemberForHours] = useState(null);
  const [selectedForSms, setSelectedForSms] = useState([]);
  const [smsMessage, setSmsMessage] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null); // {preview, errors, total}
  const [csvSelections, setCsvSelections] = useState({}); // {index: bool}
  const fileInputRef = useRef(null);
  const hoursFileInputRef = useRef(null);
  const [editCredentialsDialogOpen, setEditCredentialsDialogOpen] = useState(false);
  const [editCredentialsFormData, setEditCredentialsFormData] = useState({
    lovska_izkaznica: "",
    stevilka_clana: "",
    novo_geslo: ""
  });
  const [memberToEditCredentials, setMemberToEditCredentials] = useState(null);
  
  const [formData, setFormData] = useState({
    ime: "",
    priimek: "",
    email: "",
    telefon: "",
    lovska_izkaznica: "",
    stevilka_clana: "",
    vloga: "clan",
    geslo: "",
    upravljana_koca: "",
    obvezne_ure: 20,
    spol: "M",
    rojstni_dan: "",
    datum_vstopa: "",
    naslov: "",
    kraj: "",
    skupina: "",
    skupina_id: "",
    mentor_id: "",
    mentor_id_2: "",
    arhiv_access: false,
    je_preglednik: false,
    je_lovski_cuvaj: false
  });

  const [debtForm, setDebtForm] = useState({
    clan_id: "",
    tip: "clanarina",
    opis: "",
    znesek: 0,
    datum: new Date().toISOString().split('T')[0],
    leto: new Date().getFullYear()
  });

  // All available roles
  const roles = [
    { value: "predsednik", label: "Predsednik" },
    { value: "gospodar", label: "Gospodar" },
    { value: "blagajnik", label: "Blagajnik" },
    { value: "tajnik", label: "Tajnik" },
    { value: "upravnik_lovske_koce", label: "Upravnik lovske koče" },
    { value: "skupinovodja", label: "Skupinovodja" },
    { value: "kinoloski_referent", label: "Kinološki referent" },
    { value: "referent_strelstvo", label: "Referent za strelstvo" },
    { value: "komisija_skode", label: "Komisija za škode" },
    { value: "komisija_izobrazevanje", label: "Komisija za izobraževanje" },
    { value: "komisija_trofeje", label: "Komisija za ocenjevanje trofej" },
    { value: "disciplinska_komisija", label: "Disciplinska komisija" },
    { value: "pripravnik", label: "Pripravnik" },
    { value: "clan", label: "Član" }
  ];

  const debtTypes = [
    { value: "clanarina", label: "Članarina" },
    { value: "odkup_divjacine", label: "Odkup divjačine" },
    { value: "kazen", label: "Kazen" },
    { value: "placilo", label: "Plačilo (odbitek)" },
    { value: "drugo", label: "Drugo" }
  ];

  // Check if user can manage members
  const canManageMembers = hasRole(["administrator", "predsednik", "tajnik"]);
  const canViewAll = hasRole(["administrator", "predsednik", "tajnik", "gospodar", "blagajnik"]);
  const canManageFinances = hasRole(["administrator", "predsednik", "blagajnik"]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Use full users endpoint for managers, directory for regular members
      const usersEndpoint = canViewAll ? `${API}/users` : `${API}/users/directory`;
      
      const [membersRes, debtsRes, groupsRes] = await Promise.all([
        axios.get(usersEndpoint, { headers }),
        // Only predsednik and blagajnik can see all debts
        canManageFinances
          ? axios.get(`${API}/debts/summary/all`, { headers })
          : Promise.resolve({ data: [] }),
        axios.get(`${API}/ld-groups`, { headers }).catch(() => ({ data: [] })),
      ]);
      setMembers(membersRes.data);
      setDebtsSummary(debtsRes.data);
      setLdGroups(groupsRes.data || []);
    } catch (error) {
      toast.error("Napaka pri nalaganju podatkov");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingMember) {
        const updateData = {
          ime: formData.ime,
          priimek: formData.priimek,
          email: formData.email,
          telefon: formData.telefon,
          vloga: formData.vloga,
          obvezne_ure: formData.obvezne_ure,
          spol: formData.spol,
          rojstni_dan: formData.rojstni_dan || null,
          datum_vstopa: formData.datum_vstopa || null,
          naslov: formData.naslov || null,
          kraj: formData.kraj || null,
          skupina: formData.skupina || "",
          skupina_id: formData.skupina_id || "",
          mentor_id: formData.vloga === "pripravnik" ? (formData.mentor_id || null) : null,
          mentor_id_2: formData.vloga === "pripravnik" ? (formData.mentor_id_2 || null) : null,
          arhiv_access: !!formData.arhiv_access,
          je_preglednik: !!formData.je_preglednik,
          je_lovski_cuvaj: !!formData.je_lovski_cuvaj
        };
        if (formData.vloga === "upravnik_lovske_koce") {
          updateData.upravljana_koca = formData.upravljana_koca;
        }
        await axios.put(`${API}/users/${editingMember.id}`, updateData, { headers });
        toast.success("Član posodobljen");
      } else {
        await axios.post(`${API}/users`, formData, { headers });
        toast.success("Član dodan");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  // Open edit credentials dialog
  const handleEditCredentials = (member) => {
    setMemberToEditCredentials(member);
    setEditCredentialsFormData({
      lovska_izkaznica: member.lovska_izkaznica || "",
      stevilka_clana: member.stevilka_clana || "",
      novo_geslo: ""
    });
    setEditCredentialsDialogOpen(true);
  };

  // Submit credential changes
  const handleEditCredentialsSubmit = async (e) => {
    e.preventDefault();
    if (!memberToEditCredentials) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const updateData = {
        lovska_izkaznica: editCredentialsFormData.lovska_izkaznica,
        stevilka_clana: editCredentialsFormData.stevilka_clana
      };
      
      // Only include password if provided
      if (editCredentialsFormData.novo_geslo && editCredentialsFormData.novo_geslo.trim()) {
        updateData.geslo = editCredentialsFormData.novo_geslo;
      }
      
      await axios.put(`${API}/users/${memberToEditCredentials.id}/credentials`, updateData, { headers });
      
      toast.success("Podatki posodobljeni");
      setEditCredentialsDialogOpen(false);
      setMemberToEditCredentials(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri posodabljanju");
    }
  };

  const handleDebtSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const finalAmount = debtForm.tip === "placilo" 
        ? -Math.abs(debtForm.znesek) 
        : Math.abs(debtForm.znesek);
      
      await axios.post(`${API}/debts`, {
        ...debtForm,
        znesek: finalAmount
      }, { headers });
      
      toast.success("Obveznost dodana");
      setDebtDialogOpen(false);
      resetDebtForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim()) {
      toast.error("Vnesite sporočilo");
      return;
    }
    if (selectedForSms.length === 0) {
      toast.error("Izberite vsaj enega prejemnika");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/sms/send`, {
        prejemniki: selectedForSms,
        sporocilo: smsMessage
      }, { headers });
      
      toast.success(response.data.message);
      setSmsDialogOpen(false);
      setSmsMessage("");
      setSelectedForSms([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri pošiljanju SMS");
    }
  };

  const toggleSmsRecipient = (memberId) => {
    setSelectedForSms(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const selectAllForSms = () => {
    const membersWithPhone = members.filter(m => m.telefon);
    setSelectedForSms(membersWithPhone.map(m => m.id));
  };

  const handleDelete = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/users/${id}`, { headers });
      toast.success("Član izbrisan");
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri brisanju");
    }
  };

  const openDeleteDialog = (member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/users/import-csv`, formData, { headers });
      const data = response.data;
      
      if (data.preview && data.preview.length > 0) {
        setCsvPreview(data);
        // Select all by default
        const sel = {};
        data.preview.forEach((_, i) => { sel[i] = true; });
        setCsvSelections(sel);
        const changed = data.preview.filter(p => p.action !== "brez_sprememb").length;
        toast.info(`${data.total} članov v CSV, ${changed} s spremembami`);
      } else {
        toast.info("Ni novih sprememb za uvoz");
        if (data.errors?.length > 0) {
          setImportResult({ imported: 0, errors: data.errors });
        }
      }
    } catch (error) {
      toast.error("Napaka pri uvozu CSV");
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirmCSVImport = async () => {
    if (!csvPreview) return;
    const selectedItems = csvPreview.preview
      .filter((_, i) => csvSelections[i])
      .map(item => ({ action: item.action, user_id: item.user_id, data: item.data }));
    
    if (selectedItems.length === 0) {
      toast.error("Izberite vsaj enega člana");
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/users/import-csv/confirm`, { items: selectedItems }, { headers });
      const d = response.data;
      toast.success(`Uvoženih: ${d.imported}, Posodobljenih: ${d.updated}`);
      setImportResult(d);
      setCsvPreview(null);
      setCsvSelections({});
      fetchData();
    } catch (error) {
      toast.error("Napaka pri potrditvi uvoza");
    }
  };

  const handleImportHoursCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/users/import-hours-csv`, formData, { headers });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Napaka pri uvozu ur");
    }
    
    if (hoursFileInputRef.current) {
      hoursFileInputRef.current.value = '';
    }
  };

  const handleExportMembers = async () => {
    try {
      const res = await fetch(`${API}/download/members-csv?token=${token}`);
      if (!res.ok) throw new Error('Napaka');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'clani_ld_rakek.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("CSV izvožen");
    } catch (error) {
      toast.error("Napaka pri izvozu");
    }
  };

  const handleExportMembersPdf = async () => {
    try {
      const res = await fetch(`${API}/download/members-pdf?token=${token}`);
      if (!res.ok) throw new Error('Napaka');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'clani_ld_rakek.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("PDF izvožen");
    } catch (error) {
      toast.error("Napaka pri izvozu PDF");
    }
  };

  const handleUpdateMandatoryHours = async (memberId, hours) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/users/${memberId}/mandatory-hours`, { obvezne_ure: hours }, { headers });
      toast.success("Obvezne ure posodobljene");
      fetchData();
      setHoursDialogOpen(false);
      setSelectedMemberForHours(null);
    } catch (error) {
      toast.error("Napaka pri posodabljanju");
    }
  };

  const downloadTemplate = () => {
    const csv = "ime,priimek,email,telefon,lovska_izkaznica,stevilka_clana,vloga\n" +
                "Janez,Novak,janez@email.si,040123456,LI-001,001,clan\n";
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'clani_predloga.csv';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { window.URL.revokeObjectURL(url); document.body.removeChild(a); }, 100);
  };

  const resetForm = () => {
    setFormData({
      ime: "",
      priimek: "",
      email: "",
      telefon: "",
      lovska_izkaznica: "",
      stevilka_clana: "",
      vloga: "clan",
      geslo: "",
      upravljana_koca: "",
      obvezne_ure: 20,
      spol: "M",
      rojstni_dan: "",
      skupina: "",
      mentor_id: "",
      mentor_id_2: "",
      arhiv_access: false,
      je_preglednik: false,
      je_lovski_cuvaj: false
    });
    setEditingMember(null);
  };

  const resetDebtForm = () => {
    setDebtForm({
      clan_id: "",
      tip: "clanarina",
      opis: "",
      znesek: 0,
      datum: new Date().toISOString().split('T')[0],
      leto: new Date().getFullYear()
    });
    setSelectedMemberForDebt(null);
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      ime: member.ime,
      priimek: member.priimek,
      email: member.email || "",
      telefon: member.telefon || "",
      lovska_izkaznica: member.lovska_izkaznica,
      stevilka_clana: member.stevilka_clana,
      vloga: member.vloga,
      geslo: "",
      upravljana_koca: member.upravljana_koca || "",
      obvezne_ure: member.obvezne_ure || 20,
      spol: member.spol || "M",
      rojstni_dan: member.rojstni_dan || "",
      datum_vstopa: member.datum_vstopa || "",
      naslov: member.naslov || "",
      kraj: member.kraj || "",
      skupina: member.skupina || "",
      skupina_id: member.skupina_id || "",
      mentor_id: member.mentor_id || "",
      mentor_id_2: member.mentor_id_2 || "",
      arhiv_access: !!member.arhiv_access,
      je_preglednik: !!member.je_preglednik,
      je_lovski_cuvaj: !!member.je_lovski_cuvaj
    });
    setDialogOpen(true);
  };

  const openDebtDialog = (member) => {
    setSelectedMemberForDebt(member);
    setDebtForm({
      ...debtForm,
      clan_id: member.id
    });
    setDebtDialogOpen(true);
  };

  const getRoleLabel = (role) => {
    return roles.find(r => r.value === role)?.label || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      predsednik: "bg-purple-100 text-purple-800",
      gospodar: "bg-green-100 text-green-800",
      blagajnik: "bg-blue-100 text-blue-800",
      tajnik: "bg-orange-100 text-orange-800",
      upravnik_lovske_koce: "bg-amber-100 text-amber-800",
      skupinovodja: "bg-teal-100 text-teal-800",
      kinoloski_referent: "bg-cyan-100 text-cyan-800",
      referent_strelstvo: "bg-red-100 text-red-800",
      komisija_skode: "bg-pink-100 text-pink-800",
      komisija_izobrazevanje: "bg-indigo-100 text-indigo-800",
      komisija_trofeje: "bg-yellow-100 text-yellow-800",
      disciplinska_komisija: "bg-rose-100 text-rose-800",
      clan: "bg-gray-100 text-gray-800"
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sl-SI', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getMemberDebt = (memberId) => {
    const debt = debtsSummary.find(d => d.clan_id === memberId);
    return debt?.skupni_dolg || 0;
  };

  // Only predsednik and blagajnik can see all debts
  const canViewAllDebts = hasRole(["predsednik", "blagajnik"]);
  // Only predsednik and blagajnik can manage debts
  const canManageDebts = hasRole(["predsednik", "blagajnik"]);
  // Only predsednik and gospodar can manage hours
  const canManageHours = hasRole(["predsednik", "gospodar"]);

  // Filter members by search and sort by priimek
  const filteredMembers = members.filter(m => {
    const fullName = `${m.ime} ${m.priimek}`.toLowerCase();
    const phone = m.telefon?.toLowerCase() || "";
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || phone.includes(query);
  }).sort((a, b) => a.priimek.localeCompare(b.priimek, 'sl'));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="members-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
            <Users className="h-6 w-6 sm:h-8 sm:w-8" />
            {canViewAll ? "Člani" : "Imenik članov"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {members.length} članov v lovski družini
            {!canViewAll && " • samo branje"}
          </p>
        </div>
        
        {/* Action buttons - only for predsednik and tajnik */}
        {canManageMembers && (
          <div className="flex flex-wrap items-center gap-2">
            {hasRole(["predsednik", "tajnik", "gospodar"]) && (
              <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="bg-green-50 border-green-200" data-testid="sms-btn">
                    <MessageSquare className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">SMS</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg mx-4">
                  <DialogHeader>
                    <DialogTitle>Pošlji SMS</DialogTitle>
                    <DialogDescription>
                      Izberite prejemnike in napišite sporočilo
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Prejemniki ({selectedForSms.length})</Label>
                        <Button type="button" variant="link" size="sm" onClick={selectAllForSms}>
                          Izberi vse
                        </Button>
                      </div>
                      <div className="max-h-48 overflow-auto border rounded-lg p-2 space-y-1">
                        {members.filter(m => m.telefon).map((member) => (
                          <label 
                            key={member.id} 
                            className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedForSms.includes(member.id)}
                              onChange={() => toggleSmsRecipient(member.id)}
                              className="rounded"
                            />
                            <span className="flex-1 text-sm">{member.ime} {member.priimek}</span>
                            <span className="text-xs text-muted-foreground">{member.telefon}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Sporočilo</Label>
                      <Textarea
                        value={smsMessage}
                        onChange={(e) => setSmsMessage(e.target.value)}
                        placeholder="Napišite SMS sporočilo..."
                        rows={3}
                        maxLength={160}
                      />
                      <p className="text-xs text-muted-foreground text-right">
                        {smsMessage.length}/160
                      </p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                      <strong>Demo način:</strong> SMS pošiljanje je v demonstracijskem načinu.
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>
                      Prekliči
                    </Button>
                    <Button onClick={handleSendSms} disabled={selectedForSms.length === 0 || !smsMessage.trim()}>
                      Pošlji ({selectedForSms.length})
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            <Button variant="outline" size="sm" onClick={handleExportMembers} data-testid="export-csv-btn">
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Izvozi CSV</span>
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleExportMembersPdf} data-testid="export-pdf-btn" className="bg-red-50 border-red-200 hover:bg-red-100">
              <FileText className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Izvozi PDF</span>
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleImportCSV}
            />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} title="Uvozi CSV izvožen iz Lisjaka (LZS sistem)">
              <Upload className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Uvozi iz Lisjaka</span>
            </Button>

            {canManageHours && (
              <>
                <input
                  ref={hoursFileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleImportHoursCSV}
                />
                <Button variant="outline" size="sm" onClick={() => hoursFileInputRef.current?.click()} className="bg-blue-50 border-blue-200">
                  <Clock className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Uvozi ure</span>
                </Button>
              </>
            )}

            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-member-btn">
                  <Plus className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Dodaj</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingMember ? "Uredi člana" : "Nov član"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Ime</Label>
                      <Input
                        value={formData.ime}
                        onChange={(e) => setFormData({ ...formData, ime: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Priimek</Label>
                      <Input
                        value={formData.priimek}
                        onChange={(e) => setFormData({ ...formData, priimek: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  {!editingMember && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Št. lovske izkaznice</Label>
                        <Input
                          value={formData.lovska_izkaznica}
                          onChange={(e) => setFormData({ ...formData, lovska_izkaznica: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Številka člana</Label>
                        <Input
                          value={formData.stevilka_clana}
                          onChange={(e) => setFormData({ ...formData, stevilka_clana: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Telefon</Label>
                      <Input
                        value={formData.telefon}
                        onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Funkcija</Label>
                    <Select 
                      onValueChange={(value) => setFormData({ ...formData, vloga: value })} 
                      value={formData.vloga}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.vloga === "upravnik_lovske_koce" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Upravljana koča</Label>
                      <Select 
                        onValueChange={(value) => setFormData({ ...formData, upravljana_koca: value })} 
                        value={formData.upravljana_koca}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Izberite kočo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pranger">Koča Pranger</SelectItem>
                          <SelectItem value="rakov_skocjan">Koča Rakov Škocjan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-xs">Obvezne ure na leto</Label>
                    <Input
                      type="number"
                      value={formData.obvezne_ure}
                      onChange={(e) => setFormData({ ...formData, obvezne_ure: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Spol</Label>
                    <Select value={formData.spol} onValueChange={(val) => setFormData({ ...formData, spol: val })}>
                      <SelectTrigger data-testid="spol-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Moški</SelectItem>
                        <SelectItem value="Z">Ženski</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Rojstni dan</Label>
                    <Input
                      type="date"
                      value={formData.rojstni_dan}
                      onChange={(e) => setFormData({ ...formData, rojstni_dan: e.target.value })}
                      data-testid="rojstni-dan-input"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Datum vstopa v LD</Label>
                    <Input
                      type="date"
                      value={formData.datum_vstopa}
                      onChange={(e) => setFormData({ ...formData, datum_vstopa: e.target.value })}
                      data-testid="datum-vstopa-input"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Skupina / revir</Label>
                    <Select
                      value={formData.skupina_id || "none"}
                      onValueChange={(val) => setFormData({ ...formData, skupina_id: val === "none" ? "" : val })}
                    >
                      <SelectTrigger data-testid="skupina-select">
                        <SelectValue placeholder="Ni dodeljena" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Ni dodeljena</SelectItem>
                        {ldGroups.map(g => (
                          <SelectItem key={g.id} value={g.id}>
                            {g.ime}{g.revir_naziv ? ` – ${g.revir_naziv}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {ldGroups.length === 0 && (
                      <p className="text-[10px] text-muted-foreground">Ni ustvarjenih skupin. Ustvari jih v <b>Upravljanje → Skupine in revirji</b>.</p>
                    )}
                  </div>

                  {editingMember && hasRole(["predsednik", "tajnik", "gospodar", "administrator", "super_admin"]) && (
                    <div className="flex items-center gap-2 col-span-2 pt-2 border-t border-slate-200">
                      <input
                        type="checkbox"
                        id="arhiv_access"
                        checked={!!formData.arhiv_access}
                        onChange={(e) => setFormData({ ...formData, arhiv_access: e.target.checked })}
                        className="h-4 w-4"
                        data-testid="arhiv-access-checkbox"
                      />
                      <Label htmlFor="arhiv_access" className="text-xs cursor-pointer">
                        Dostop do arhiva obvestil in dogodkov
                      </Label>
                    </div>
                  )}

                  {editingMember && hasRole(["predsednik", "tajnik", "administrator", "super_admin"]) && (
                    <div className="flex items-center gap-4 col-span-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="je_preglednik"
                          checked={!!formData.je_preglednik}
                          onChange={(e) => setFormData({ ...formData, je_preglednik: e.target.checked })}
                          className="h-4 w-4"
                          data-testid="preglednik-checkbox"
                        />
                        <Label htmlFor="je_preglednik" className="text-xs cursor-pointer">
                          Preglednik divjačine
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="je_lovski_cuvaj"
                          checked={!!formData.je_lovski_cuvaj}
                          onChange={(e) => setFormData({ ...formData, je_lovski_cuvaj: e.target.checked })}
                          className="h-4 w-4"
                          data-testid="lovski-cuvaj-checkbox"
                        />
                        <Label htmlFor="je_lovski_cuvaj" className="text-xs cursor-pointer">
                          Lovski čuvaj
                        </Label>
                      </div>
                    </div>
                  )}

                  {/* Mentor dropdowns - only when assigning to a pripravnik */}
                  {formData.vloga === "pripravnik" && hasRole(["predsednik", "tajnik", "gospodar", "administrator"]) && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs">Mentor 1</Label>
                        <Select
                          value={formData.mentor_id || "none"}
                          onValueChange={(val) => setFormData({ ...formData, mentor_id: val === "none" ? "" : val })}
                        >
                          <SelectTrigger data-testid="mentor-select" className="h-auto min-h-9 py-1.5 [&>span]:line-clamp-2 [&>span]:whitespace-normal [&>span]:text-left">
                            <SelectValue placeholder="Brez mentorja" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Brez mentorja</SelectItem>
                            {members
                              .filter(m => m.vloga !== "pripravnik" && m.id !== editingMember?.id)
                              .sort((a, b) => a.priimek.localeCompare(b.priimek, "sl"))
                              .map(m => (
                                <SelectItem key={m.id} value={m.id} className="whitespace-normal break-words">
                                  {m.ime} {m.priimek}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Mentor 2 (opcijsko)</Label>
                        <Select
                          value={formData.mentor_id_2 || "none"}
                          onValueChange={(val) => setFormData({ ...formData, mentor_id_2: val === "none" ? "" : val })}
                        >
                          <SelectTrigger data-testid="mentor-select-2" className="h-auto min-h-9 py-1.5 [&>span]:line-clamp-2 [&>span]:whitespace-normal [&>span]:text-left">
                            <SelectValue placeholder="Brez drugega mentorja" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Brez drugega mentorja</SelectItem>
                            {members
                              .filter(m => m.vloga !== "pripravnik" && m.id !== editingMember?.id && m.id !== formData.mentor_id)
                              .sort((a, b) => a.priimek.localeCompare(b.priimek, "sl"))
                              .map(m => (
                                <SelectItem key={m.id} value={m.id} className="whitespace-normal break-words">
                                  {m.ime} {m.priimek}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Naslov</Label>
                      <Input
                        value={formData.naslov}
                        onChange={(e) => setFormData({ ...formData, naslov: e.target.value })}
                        placeholder="Ulica in hišna št."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Kraj</Label>
                      <Input
                        value={formData.kraj}
                        onChange={(e) => setFormData({ ...formData, kraj: e.target.value })}
                        placeholder="Kraj"
                      />
                    </div>
                  </div>

                  {!editingMember && (
                    <div className="space-y-1">
                      <Label className="text-xs">Začetno geslo</Label>
                      <Input
                        type="password"
                        value={formData.geslo}
                        onChange={(e) => setFormData({ ...formData, geslo: e.target.value })}
                        placeholder="Privzeto: številka člana"
                      />
                    </div>
                  )}

                  <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Prekliči
                    </Button>
                    <Button type="submit">
                      {editingMember ? "Posodobi" : "Dodaj"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Import Result */}
      {importResult && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-900">
                  Uvoženih: {importResult.imported || 0}, Posodobljenih: {importResult.updated || 0}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setImportResult(null)}>×</Button>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="mt-2 text-xs text-red-600 space-y-0.5">
                {importResult.errors.slice(0, 5).map((err, i) => <div key={i}>{err}</div>)}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CSV Preview Dialog */}
      <Dialog open={!!csvPreview} onOpenChange={(open) => !open && setCsvPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Pregled uvoza - {csvPreview?.total || 0} članov</DialogTitle>
            {csvPreview?.cross_ld_count > 0 && (
              <div className="bg-red-50 border border-red-300 rounded p-2 mt-2 text-sm" data-testid="csv-cross-ld-banner">
                <div className="font-semibold text-red-800">
                  ⚠️ Pozor: {csvPreview.cross_ld_count} oseb v tem CSV-ju že obstaja v drugih LD-jih
                </div>
                <div className="text-red-700 text-xs mt-0.5">
                  Priporočamo, da <b>odznačite</b> vrstice z rdečo označbo, sicer nastanejo cross-LD podvojki.
                </div>
              </div>
            )}
            {csvPreview?.stolpci_csv && (
              <div className="text-xs text-muted-foreground pt-1">
                Stolpci v CSV: {csvPreview.stolpci_csv.join(", ")}
                {!csvPreview.stolpci_csv.some(s => s.toLowerCase().includes("rojstni") || s.toLowerCase().includes("datum rojstva")) && (
                  <span className="text-amber-600 font-medium ml-2">Manjka stolpec za rojstni dan!</span>
                )}
                {!csvPreview.stolpci_csv.some(s => s.toLowerCase().includes("vstop") || s.toLowerCase().includes("vpisa")) && (
                  <span className="text-amber-600 font-medium ml-2">Manjka stolpec za datum vstopa!</span>
                )}
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={csvPreview?.preview?.length > 0 && csvPreview?.preview?.every((_, i) => csvSelections[i])}
                  onCheckedChange={(checked) => {
                    const sel = {};
                    csvPreview?.preview?.forEach((_, i) => { sel[i] = !!checked; });
                    setCsvSelections(sel);
                  }}
                  data-testid="csv-select-all"
                />
                <span className="text-sm font-medium">Izberi vse</span>
              </label>
              <div className="text-xs text-muted-foreground space-x-2">
                <span>{csvPreview?.preview?.filter(p => p.action === "brez_sprememb").length || 0} brez sprememb</span>
                <span>|</span>
                <span className="font-medium">{Object.values(csvSelections).filter(Boolean).length} izbranih za uvoz</span>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto border rounded-md" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            <div className="space-y-2 pr-4">
              {csvPreview?.preview?.map((item, idx) => (
                <div key={idx} className={`p-3 rounded-lg border transition-all ${
                  item.action === "brez_sprememb" 
                    ? 'border-muted/50 bg-muted/20' 
                    : csvSelections[idx] 
                      ? 'border-primary bg-primary/5' 
                      : 'border-muted bg-muted/30 opacity-60'
                }`}>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={!!csvSelections[idx]}
                      onCheckedChange={(checked) => setCsvSelections(prev => ({ ...prev, [idx]: !!checked }))}
                      data-testid={`csv-select-${idx}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{item.ime_priimek}</span>
                        <Badge variant={
                          item.action === "nov" ? "default" : 
                          item.action === "posodobi" ? "secondary" : "outline"
                        } className="text-[10px]">
                          {item.action === "nov" ? "Nov član" : item.action === "posodobi" ? "Posodobitev" : "Brez sprememb"}
                        </Badge>
                        <span className="text-xs text-muted-foreground">LI: {item.lovska_izkaznica}</span>
                      </div>
                      {/* Cross-LD warning: član obstaja v drugi LD */}
                      {item.cross_ld && (
                        <div className="mt-1 p-2 bg-red-50 border border-red-300 rounded text-xs" data-testid={`csv-cross-ld-warning-${idx}`}>
                          <div className="font-semibold text-red-800 flex items-center gap-1">
                            ⚠️ Ta oseba že obstaja v LD <span className="font-mono">{item.cross_ld.obstaja_v_ld}</span>
                          </div>
                          <div className="text-red-700 mt-0.5">
                            Kot: <b>{item.cross_ld.ime_priimek || "—"}</b>. Uvoz v vašo LD ustvari podvojen zapis.
                          </div>
                        </div>
                      )}
                      {/* Show warnings for credential/name differences */}
                      {item.warnings && Object.keys(item.warnings).length > 0 && (
                        <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-xs space-y-0.5">
                          <div className="font-medium text-amber-800">Opozorilo - razlike v prijavnih podatkih (se NE posodobijo):</div>
                          {Object.entries(item.warnings).map(([field, val]) => (
                            <div key={field} className="flex gap-1 text-amber-700">
                              <span className="font-medium w-28">{field}:</span>
                              <span>baza: <strong>{val.v_bazi}</strong></span>
                              <span className="mx-1">|</span>
                              <span>CSV: <strong>{val.v_csv}</strong></span>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Show changes for updates */}
                      {item.action === "posodobi" && Object.keys(item.changes).length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {Object.entries(item.changes).map(([field, val]) => (
                            <div key={field} className="text-xs flex gap-1">
                              <span className="text-muted-foreground font-medium w-24">{field}:</span>
                              <span className="text-red-500 line-through">{val.staro || '—'}</span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600 font-medium">{val.novo}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Show new member data */}
                      {item.action === "nov" && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {item.data.email && <span className="mr-3">{item.data.email}</span>}
                          {item.data.telefon && <span className="mr-3">{item.data.telefon}</span>}
                          {item.data.spol && <span className="mr-3">Spol: {item.data.spol}</span>}
                          {item.data.rojstni_dan && <span className="mr-3">Roj: {item.data.rojstni_dan}</span>}
                          {item.data.datum_vstopa && <span>Vstop: {item.data.datum_vstopa}</span>}
                        </div>
                      )}
                      {/* Show existing data for unchanged members */}
                      {item.action === "brez_sprememb" && item.existing && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {item.existing.email && <span className="mr-3">{item.existing.email}</span>}
                          {item.existing.telefon && <span className="mr-3">{item.existing.telefon}</span>}
                          {item.existing.spol && <span className="mr-3">Spol: {item.existing.spol}</span>}
                          {item.existing.vloga && <span className="mr-3">{item.existing.vloga}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {csvPreview?.errors?.length > 0 && (
            <div className="text-xs text-red-600 space-y-0.5 max-h-16 overflow-auto">
              {csvPreview.errors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
          )}
          <DialogFooter className="gap-2">
            <div className="flex-1 text-xs text-muted-foreground">
              {Object.values(csvSelections).filter(Boolean).length} od {csvPreview?.total || 0} izbranih
            </div>
            <Button variant="outline" onClick={() => setCsvPreview(null)}>Prekliči</Button>
            <Button onClick={handleConfirmCSVImport} data-testid="confirm-csv-import-btn">
              Potrdi uvoz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Išči po imenu ali telefonu..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="search-members-input"
        />
      </div>

      {/* Members List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredMembers.map((member) => {
          const debt = getMemberDebt(member.id);
          return (
            <Card 
              key={member.id} 
              className="hover:shadow-md transition-shadow"
              data-testid={`member-card-${member.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">
                      {member.ime} {member.priimek}
                    </h3>
                    
                    {/* Role badge */}
                    <Badge className={`${getRoleBadgeColor(member.vloga)} text-xs mt-1`}>
                      {getRoleLabel(member.vloga)}
                    </Badge>
                    {member.je_preglednik && (
                      <Badge
                        className="bg-emerald-100 text-emerald-800 text-xs mt-1 ml-1"
                        data-testid={`preglednik-badge-${member.id}`}
                        title="Preglednik divjačine"
                      >
                        Preglednik divjačine
                      </Badge>
                    )}
                    {member.je_lovski_cuvaj && (
                      <Badge
                        className="bg-amber-100 text-amber-800 text-xs mt-1 ml-1"
                        data-testid={`lovski-cuvaj-badge-${member.id}`}
                        title="Lovski čuvaj"
                      >
                        Lovski čuvaj
                      </Badge>
                    )}
                    {member.is_test && (
                      <Badge
                        className="bg-amber-100 text-amber-900 border border-amber-300 text-xs mt-1 ml-1"
                        data-testid={`test-member-badge-${member.id}`}
                        title="Sandbox testni član – izključen iz štetja in izvozov"
                      >
                        TEST (sandbox)
                      </Badge>
                    )}
                    
                    {/* Phone */}
                    {member.telefon ? (
                      <a 
                        href={`tel:${member.telefon}`}
                        className="text-sm text-primary flex items-center gap-1 mt-2 hover:underline"
                      >
                        <Phone className="h-3 w-3" />
                        {member.telefon}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-2">
                        Ni telefonske številke
                      </p>
                    )}

                    {/* Leadership-only info: tenure, birthdate, entry date, group */}
                    {canViewAll && (
                      <div className="mt-2 space-y-0.5 text-xs text-muted-foreground border-t pt-2">
                        {member.datum_vstopa && formatSloDate(member.datum_vstopa) && (
                          <p>
                            Vstop: {formatSloDate(member.datum_vstopa)}
                            {member.leta_v_ld != null && (
                              <span className="ml-1 text-primary font-medium">(staž {member.leta_v_ld} let)</span>
                            )}
                          </p>
                        )}
                        {member.rojstni_dan && formatSloDate(member.rojstni_dan) && (
                          <p>
                            Rojen: {formatSloDate(member.rojstni_dan)}
                            {member.starost != null && (
                              <span className="ml-1">({member.starost} let)</span>
                            )}
                          </p>
                        )}
                        {(() => {
                          const g = ldGroups.find(x => x.id === member.skupina_id);
                          if (g) {
                            return (
                              <p>Skupina: <span className="font-medium text-primary">{g.ime}{g.revir_naziv ? ` (${g.revir_naziv})` : ""}</span></p>
                            );
                          }
                          if (member.skupina) {
                            return (
                              <p>Skupina: <span className="font-medium text-primary">{member.skupina}.</span></p>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}

                    {/* Realizacija ur za tega člana */}
                    {canViewAll && (() => {
                      const opravljene = Number(member.opravljene_ure || 0);
                      const obvezne = Number(member.izracunane_ure || member.obvezne_ure || 0);
                      const pct = obvezne > 0 ? Math.min(100, Math.round((opravljene / obvezne) * 100)) : (opravljene > 0 ? 100 : 0);
                      const barColor = obvezne === 0
                        ? "bg-blue-500"
                        : pct >= 100 ? "bg-green-500" : pct >= 50 ? "bg-yellow-500" : "bg-red-500";
                      return (
                        <div className="mt-2 pt-2 border-t" data-testid={`member-hours-${member.id}`}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Ure
                            </span>
                            <span className="font-semibold">
                              {opravljene}{obvezne > 0 ? ` / ${obvezne}` : ""} h
                              {obvezne > 0 && (
                                <span className="ml-1 text-muted-foreground font-normal">({pct}%)</span>
                              )}
                              {obvezne === 0 && opravljene > 0 && (
                                <span className="ml-1 text-blue-600 text-[10px]">opravičen</span>
                              )}
                              {obvezne === 0 && opravljene === 0 && (
                                <span className="ml-1 text-blue-600 text-[10px]">opravičen</span>
                              )}
                            </span>
                          </div>
                          {obvezne > 0 && (
                            <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Debt - only visible to predsednik and blagajnik */}
                    {canViewAllDebts && debt > 0 && (
                      <Badge variant="destructive" className="mt-2 text-xs">
                        Dolg: {formatCurrency(debt)}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex flex-col gap-1">
                    {/* Only blagajnik and predsednik can add debts */}
                    {canManageDebts && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => openDebtDialog(member)}
                        title="Dodaj obveznost"
                      >
                        <Receipt className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Only predsednik and tajnik can edit/delete */}
                    {canManageMembers && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => openEditDialog(member)}
                          data-testid={`edit-member-${member.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleEditCredentials(member)}
                          data-testid={`edit-credentials-${member.id}`}
                          title="Uredi prijavo"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive"
                          onClick={() => openDeleteDialog(member)}
                          data-testid={`delete-member-${member.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Credentials Dialog */}
      <Dialog open={editCredentialsDialogOpen} onOpenChange={setEditCredentialsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Uredi prijavo</DialogTitle>
            <DialogDescription>
              {memberToEditCredentials && `Spremenite prijavne podatke za ${memberToEditCredentials.ime} ${memberToEditCredentials.priimek}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCredentialsSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_lovska">Številka lovske izkaznice</Label>
              <Input
                id="edit_lovska"
                value={editCredentialsFormData.lovska_izkaznica}
                onChange={(e) => setEditCredentialsFormData({ ...editCredentialsFormData, lovska_izkaznica: e.target.value })}
                placeholder="Npr. 12345"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_clana">Številka člana (geslo)</Label>
              <Input
                id="edit_clana"
                value={editCredentialsFormData.stevilka_clana}
                onChange={(e) => setEditCredentialsFormData({ ...editCredentialsFormData, stevilka_clana: e.target.value })}
                placeholder="Npr. 001"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="novo_geslo">Novo geslo (opcijsko)</Label>
              <Input
                id="novo_geslo"
                type="password"
                value={editCredentialsFormData.novo_geslo}
                onChange={(e) => setEditCredentialsFormData({ ...editCredentialsFormData, novo_geslo: e.target.value })}
                placeholder="Pustite prazno za brez spremembe"
              />
              <p className="text-xs text-muted-foreground">
                Če pustite prazno, bo geslo ostalo nespremenjeno
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditCredentialsDialogOpen(false)}>
                Prekliči
              </Button>
              <Button type="submit">
                Shrani
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Empty state */}
      {filteredMembers.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchQuery ? "Ni rezultatov iskanja" : "Ni članov"}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Debt Dialog - only for predsednik and blagajnik */}
      <Dialog open={debtDialogOpen} onOpenChange={(open) => { setDebtDialogOpen(open); if (!open) resetDebtForm(); }}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>Nova obveznost</DialogTitle>
            <DialogDescription>
              {selectedMemberForDebt && `${selectedMemberForDebt.ime} ${selectedMemberForDebt.priimek}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDebtSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs">Tip obveznosti</Label>
              <Select 
                onValueChange={(value) => setDebtForm({ ...debtForm, tip: value })} 
                value={debtForm.tip}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {debtTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Znesek (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={debtForm.znesek}
                  onChange={(e) => setDebtForm({ ...debtForm, znesek: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Leto</Label>
                <Input
                  type="number"
                  value={debtForm.leto}
                  onChange={(e) => setDebtForm({ ...debtForm, leto: parseInt(e.target.value) || new Date().getFullYear() })}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Datum</Label>
              <Input
                type="date"
                value={debtForm.datum}
                onChange={(e) => setDebtForm({ ...debtForm, datum: e.target.value })}
                required
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Opis</Label>
              <Input
                value={debtForm.opis}
                onChange={(e) => setDebtForm({ ...debtForm, opis: e.target.value })}
                placeholder="npr. Članarina 2024"
                required
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => { setDebtDialogOpen(false); resetDebtForm(); }}>
                Prekliči
              </Button>
              <Button type="submit">Dodaj</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Member Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-sm mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>Izbriši člana?</AlertDialogTitle>
            <AlertDialogDescription>
              Ali ste prepričani, da želite izbrisati člana &bdquo;{memberToDelete?.ime} {memberToDelete?.priimek}&rdquo;?
              Ta akcija je nepovratna.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => setMemberToDelete(null)}>Prekliči</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(memberToDelete?.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Izbriši
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MembersPage;
